import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Receipt, Plus, Trash2, Edit, TrendingUp, TrendingDown, Building } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Scontrini = () => {
  const { user, canCreateScontrini, hasPermesso } = useAuth();
  const canDelete = hasPermesso('elimina_scontrini');
  const canEdit = hasPermesso('modifica_scontrini');
  const [scontrini, setScontrini] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedScontrino, setSelectedScontrino] = useState(null);
  const [formData, setFormData] = useState({
    azienda_id: user?.azienda_id || '',
    descrizione: '',
    importo: '',
    tipo: 'ENTRATA',
    data_operazione: new Date().toISOString().split('T')[0]
  });
  const [editData, setEditData] = useState({});

  useEffect(() => {
    if (user?.azienda_id) {
      fetchData();
    }
  }, [user]);

  const fetchData = async () => {
    try {
      const response = await axios.get(`${API_URL}/scontrini`);
      setScontrini(response.data);
    } catch (error) {
      toast.error('Errore nel caricamento dei dati');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_URL}/scontrini`, {
        ...formData,
        azienda_id: user.azienda_id,
        importo: parseFloat(formData.importo)
      });
      toast.success('Scontrino inserito');
      setIsDialogOpen(false);
      setFormData({ azienda_id: user.azienda_id, descrizione: '', importo: '', tipo: 'ENTRATA', data_operazione: new Date().toISOString().split('T')[0] });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nell\'inserimento');
    }
  };

  const handleEdit = async (e) => {
    e.preventDefault();
    try {
      await axios.patch(`${API_URL}/scontrini/${selectedScontrino.id}`, {
        ...editData,
        importo: editData.importo ? parseFloat(editData.importo) : undefined
      });
      toast.success('Scontrino aggiornato');
      setEditDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error('Errore nell\'aggiornamento');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Sei sicuro di voler eliminare questo scontrino?')) return;
    try {
      await axios.delete(`${API_URL}/scontrini/${id}`);
      toast.success('Scontrino eliminato');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nell\'eliminazione');
    }
  };

  const openEditDialog = (scontrino) => {
    setSelectedScontrino(scontrino);
    setEditData({
      descrizione: scontrino.descrizione,
      importo: scontrino.importo,
      tipo: scontrino.tipo,
      data_operazione: scontrino.data_operazione,
      note: scontrino.note
    });
    setEditDialogOpen(true);
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('it-IT', {
      style: 'currency',
      currency: 'EUR'
    }).format(value);
  };

  const entrate = scontrini.filter(s => s.tipo === 'ENTRATA');
  const uscite = scontrini.filter(s => s.tipo === 'USCITA');
  const totaleEntrate = entrate.reduce((acc, s) => acc + s.importo, 0);
  const totaleUscite = uscite.reduce((acc, s) => acc + s.importo, 0);
  const saldo = totaleEntrate - totaleUscite;

  if (!user?.azienda_id) {
    return (
      <div data-testid="scontrini-page" className="space-y-6">
        <Card className="border-dashed border-2">
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Building className="w-12 h-12 text-slate-300 mb-4" />
            <p className="text-slate-500">Non sei assegnato a nessuna azienda</p>
            <p className="text-sm text-slate-400">Contatta il Dirigente Sindacato per l'assegnazione</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div data-testid="scontrini-page" className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="page-header flex items-center gap-3">
            <Receipt className="w-8 h-8" />
            Scontrini - {user.azienda_nome}
          </h1>
          <p className="text-slate-600 mt-1">Gestione resoconti e scontrini della tua azienda</p>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button data-testid="create-scontrino-btn" className="bg-slate-900 hover:bg-slate-800 rounded-sm">
              <Plus className="w-4 h-4 mr-2" />
              Nuovo Scontrino
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="font-serif text-xl">Inserisci Scontrino</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label>Tipo Operazione</Label>
                <Select
                  value={formData.tipo}
                  onValueChange={(value) => setFormData({ ...formData, tipo: value })}
                >
                  <SelectTrigger data-testid="scontrino-tipo-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ENTRATA">Entrata</SelectItem>
                    <SelectItem value="USCITA">Uscita</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="importo">Importo (€)</Label>
                <Input
                  id="importo"
                  data-testid="scontrino-importo-input"
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.importo}
                  onChange={(e) => setFormData({ ...formData, importo: e.target.value })}
                  placeholder="0.00"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="descrizione">Descrizione</Label>
                <Textarea
                  id="descrizione"
                  data-testid="scontrino-desc-input"
                  value={formData.descrizione}
                  onChange={(e) => setFormData({ ...formData, descrizione: e.target.value })}
                  placeholder="Descrizione dell'operazione..."
                  rows={3}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="data">Data Operazione</Label>
                <Input
                  id="data"
                  data-testid="scontrino-data-input"
                  type="date"
                  value={formData.data_operazione}
                  onChange={(e) => setFormData({ ...formData, data_operazione: e.target.value })}
                  required
                />
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                  Annulla
                </Button>
                <Button type="submit" data-testid="submit-scontrino-btn" className="bg-slate-900 hover:bg-slate-800">
                  Inserisci
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card className="stat-card border-2 border-slate-200" data-testid="saldo-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-slate-600">Saldo Attuale</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-2">
              {saldo >= 0 ? (
                <TrendingUp className="w-6 h-6 text-green-600" />
              ) : (
                <TrendingDown className="w-6 h-6 text-red-600" />
              )}
              <span className={`text-3xl font-bold ${saldo >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                {formatCurrency(saldo)}
              </span>
            </div>
          </CardContent>
        </Card>

        <Card className="stat-card" data-testid="entrate-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-slate-600">Totale Entrate</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-2">
              <TrendingUp className="w-6 h-6 text-green-600" />
              <span className="text-3xl font-bold text-green-600">{formatCurrency(totaleEntrate)}</span>
            </div>
          </CardContent>
        </Card>

        <Card className="stat-card" data-testid="uscite-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-slate-600">Totale Uscite</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-2">
              <TrendingDown className="w-6 h-6 text-red-600" />
              <span className="text-3xl font-bold text-red-600">{formatCurrency(totaleUscite)}</span>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Transactions Tabs */}
      <Tabs defaultValue="all" className="w-full">
        <TabsList className="grid w-full grid-cols-3 max-w-md">
          <TabsTrigger value="all" data-testid="tab-all">Tutti</TabsTrigger>
          <TabsTrigger value="entrate" data-testid="tab-entrate">Entrate</TabsTrigger>
          <TabsTrigger value="uscite" data-testid="tab-uscite">Uscite</TabsTrigger>
        </TabsList>

        <TabsContent value="all" className="mt-4">
          <TransactionsTable 
            data={scontrini} 
            loading={loading} 
            canEdit={canEdit}
            canDelete={canDelete} 
            onEdit={openEditDialog} 
            onDelete={handleDelete}
            formatCurrency={formatCurrency}
          />
        </TabsContent>
        <TabsContent value="entrate" className="mt-4">
          <TransactionsTable 
            data={entrate} 
            loading={loading} 
            canEdit={canEdit}
            canDelete={canDelete} 
            onEdit={openEditDialog} 
            onDelete={handleDelete}
            formatCurrency={formatCurrency}
          />
        </TabsContent>
        <TabsContent value="uscite" className="mt-4">
          <TransactionsTable 
            data={uscite} 
            loading={loading} 
            canEdit={canEdit}
            canDelete={canDelete} 
            onEdit={openEditDialog} 
            onDelete={handleDelete}
            formatCurrency={formatCurrency}
          />
        </TabsContent>
      </Tabs>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl">Modifica Scontrino</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleEdit} className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label>Tipo Operazione</Label>
              <Select
                value={editData.tipo}
                onValueChange={(value) => setEditData({ ...editData, tipo: value })}
              >
                <SelectTrigger data-testid="edit-tipo-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ENTRATA">Entrata</SelectItem>
                  <SelectItem value="USCITA">Uscita</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-importo">Importo (€)</Label>
              <Input
                id="edit-importo"
                data-testid="edit-importo-input"
                type="number"
                step="0.01"
                min="0"
                value={editData.importo}
                onChange={(e) => setEditData({ ...editData, importo: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-descrizione">Descrizione</Label>
              <Textarea
                id="edit-descrizione"
                data-testid="edit-desc-input"
                value={editData.descrizione}
                onChange={(e) => setEditData({ ...editData, descrizione: e.target.value })}
                rows={3}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-note">Note</Label>
              <Textarea
                id="edit-note"
                data-testid="edit-note-input"
                value={editData.note || ''}
                onChange={(e) => setEditData({ ...editData, note: e.target.value })}
                rows={2}
                placeholder="Note aggiuntive..."
              />
            </div>
            <div className="flex justify-end gap-3 pt-4">
              <Button type="button" variant="outline" onClick={() => setEditDialogOpen(false)}>
                Annulla
              </Button>
              <Button type="submit" data-testid="save-edit-btn" className="bg-slate-900 hover:bg-slate-800">
                Salva Modifiche
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const TransactionsTable = ({ data, loading, canEdit, canDelete, onEdit, onDelete, formatCurrency }) => {
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  if (data.length === 0) {
    return (
      <Card className="border-dashed border-2">
        <CardContent className="flex flex-col items-center justify-center py-12">
          <Receipt className="w-12 h-12 text-slate-300 mb-4" />
          <p className="text-slate-500">Nessuno scontrino registrato</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border border-slate-200">
      <Table>
        <TableHeader>
          <TableRow className="table-header">
            <TableHead>Data</TableHead>
            <TableHead>Tipo</TableHead>
            <TableHead>Descrizione</TableHead>
            <TableHead>Importo</TableHead>
            <TableHead>Inserito da</TableHead>
            <TableHead className="text-right">Azioni</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {data.map((scontrino) => (
            <TableRow key={scontrino.id} data-testid={`scontrino-row-${scontrino.id}`}>
              <TableCell className="font-mono text-sm">
                {new Date(scontrino.data_operazione).toLocaleDateString('it-IT')}
              </TableCell>
              <TableCell>
                <span className={`inline-flex items-center px-2.5 py-0.5 text-xs font-semibold rounded-full ${
                  scontrino.tipo === 'ENTRATA' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                }`}>
                  {scontrino.tipo === 'ENTRATA' ? 'Entrata' : 'Uscita'}
                </span>
              </TableCell>
              <TableCell>
                <div>
                  <p className="font-medium">{scontrino.descrizione}</p>
                  {scontrino.note && <p className="text-xs text-slate-500 mt-1">{scontrino.note}</p>}
                </div>
              </TableCell>
              <TableCell className={`font-semibold ${scontrino.tipo === 'ENTRATA' ? 'text-green-600' : 'text-red-600'}`}>
                {scontrino.tipo === 'ENTRATA' ? '+' : '-'}{formatCurrency(scontrino.importo)}
              </TableCell>
              <TableCell className="text-sm text-slate-500">
                {scontrino.created_by_nome}
              </TableCell>
              <TableCell className="text-right">
                <div className="flex items-center justify-end gap-2">
                  {canEdit && (
                    <Button
                      variant="ghost"
                      size="sm"
                      data-testid={`edit-scontrino-${scontrino.id}`}
                      onClick={() => onEdit(scontrino)}
                    >
                      <Edit className="w-4 h-4" />
                    </Button>
                  )}
                  {canDelete && (
                    <Button
                      variant="ghost"
                      size="sm"
                      data-testid={`delete-scontrino-${scontrino.id}`}
                      onClick={() => onDelete(scontrino.id)}
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Card>
  );
};

export default Scontrini;
